<template>
  <Dialog v-model:visible="isVisible" modal header="Terms & Conditions" :style="{ width: '90vw', maxWidth: '900px' }">
    <Toast />
    <div ref="termsContentRef" class="p-4">
      <!-- PDF Viewer -->
      <PDF v-if="pdfUrl" :src="pdfUrl" class="pdf-viewer mb-6" />

      <!-- Rest of your template -->
      <div class="text-center my-4">
        <div class="flex items-center justify-center gap-2">
          <InputText id="representativeName" v-model="representativeName"
            :invalid="representativeName == ''" name="representativeName" required
            placeholder="Enter full name" />
          <span v-if="type === 'Company'" class="">OBO</span>
          <span v-if="type === 'Company'" class="font-semibold">{{ companyName }}</span>
        </div>
      </div>
      <div class="text-center my-4">
        <h3 class="text-xl font-semibold text-primary mb-1 mt-3">Please sign your name below</h3>
        <p class="text-gray-600 text-sm">Use your mouse or touch screen to sign</p>
      </div>
      <div class="bg-gray-100 p-6 my-5">
        <VueSignaturePad ref="signaturePadRef" height="300px" width="100%" :maxWidth="4" :minWidth="3"
          :disabled="isAccepted || acceptLoading" :options="{
            penColor: state.options.penColor,
            backgroundColor: state.options.backgroundColor,
            velocityFilterWeight: 0.7,  // Makes lines smoother
          }" class="max-w-[600px]  border-2 border-red-300 rounded-lg" />
      </div>
      <ProgressBar v-if="acceptLoading" mode="indeterminate"></ProgressBar>
    </div>

    <template #footer>
      <div class="flex flex-col sm:flex-row gap-2 mt-2 w-full">
        <!-- Action buttons group -->
        <div class="flex flex-col sm:flex-row gap-2 w-full sm:w-auto order-2 sm:order-1">
          <Button 
            label="Undo" 
            @click="undoSignature" 
            severity="danger" 
            :disabled="isAccepted || acceptLoading"
            class="w-full sm:w-auto" 
            outlined 
          />
          <Button 
            label="Reset Signature" 
            @click="clearSignature" 
            severity="danger"
            :disabled="isAccepted || acceptLoading" 
            class="w-full sm:w-auto"
            outlined 
          />
          <Button 
            label="Download PDF" 
            @click="downloadTerms" 
            icon="pi pi-download" 
            class="p-button-secondary w-full sm:w-auto"
            :disabled="isAccepted || acceptLoading" 
          />
        </div>

        <!-- Primary action -->
        <Button 
          :loading="acceptLoading" 
          @click="acceptTerms" 
          :disabled="isAccepted || acceptLoading"
          severity="primary"
          class="w-full sm:w-auto order-1 sm:order-2 sm:ml-auto" 
        >
          <span class="font-bolyar">Accept</span>
        </Button>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { ref, shallowRef, computed, watch, onBeforeUnmount, onMounted } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import TermsService from '../../services/TermsService';
import { VueSignaturePad } from "@selemondev/vue3-signature-pad"
import { useToast } from 'primevue/usetoast';
import PDF from "pdf-vue3";
const toast = useToast();

const representativeName = shallowRef('');

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  acceptedTerms: {
    type: Boolean,
    required: true
  },
  type: {
    type: String,
    required: true
  },
  companyName: {
    type: String,
    default: ''
  }
});

const emit = defineEmits(['update:visible', 'update:acceptedTerms', 'signature']);

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit('update:visible', value)
});

const isAccepted = computed({
  get: () => props.acceptedTerms,
  set: (value) => emit('update:acceptedTerms', value)
});

const termsContentRef = ref(null);
const pdfUrl = ref(null);

const loadPdf = async () => {
  try { 
    const response = await TermsService.getTerms();
    // Convert blob to base64
    const reader = new FileReader();
    reader.onload = () => {
      pdfUrl.value = reader.result;
    };
    reader.readAsDataURL(new Blob([response.data], { type: 'application/pdf' }));
  } catch (error) {
    console.error('Error loading PDF:', error);
  }
};

onMounted(() => {
  loadPdf();
});

watch(() => isVisible.value, async (newValue) => {
  if (newValue && !pdfUrl.value) {
    await loadPdf();
  }
});

onBeforeUnmount(() => {
  pdfUrl.value = null;
});

const downloadTerms = async () => {
  try {
    const response = await TermsService.getTerms();
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Dotventures - AffiliationSpace T&Cs.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading terms:', error);
  }
};

//signature pad
const state = ref({
  options: {
    penColor: 'rgb(0, 0, 0)',
    backgroundColor: 'rgb(255, 255, 255)'
  },
  disabled: false,
})

const signaturePadRef = ref(null);
const clearSignature = () => {
  signaturePadRef.value.clearCanvas();
}

const undoSignature = () => {
  signaturePadRef.value.undo();
}
const scrollToSignature = () => {
  setTimeout(() => {
    signaturePadRef.value?.$el.scrollIntoView({ behavior: 'smooth' });
  }, 100);
}

const acceptLoading = ref(false);

const acceptTerms = async () => {
  if (!signaturePadRef.value) {
    return;
  }
  const isCanvasEmpty = signaturePadRef.value.isCanvasEmpty();

  if (isCanvasEmpty) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please sign before accepting the terms',
      life: 3000
    });
    scrollToSignature();
    return;
  }

  if(representativeName.value == '') {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Please enter a representative name',
      life: 3000
    });
    return;
  }

  try {
    acceptLoading.value = true;
    // Get the signature data
    const signature = signaturePadRef.value.saveSignature();
    // Delay stopping the loading state
    setTimeout(() => {
      acceptLoading.value = false;
      // Emit both signature and signed PDF as Uint8Array
      emit('signature', {
        signatureImage: signature,
        representativeName: representativeName.value
      });

      isAccepted.value = true;
      isVisible.value = false;
    }, 500);
  } catch (error) {
    acceptLoading.value = false;
    console.error('Error processing signature:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Error processing signature. Please try again.',
      life: 3000
    });
  }
};
</script>

<style scoped>
.pdf-viewer {
  border: 1px solid #ddd;
  border-radius: 4px;
  width: 100%;
  height: 600px;
}
</style>